import React from 'react';
import { useQueryClient } from 'react-query';
import { EntityNameModal } from '../EntityNameModal/EntityNameModal';
import { useRenameEntityMutation } from '../../api/useRenameEntityMutation';
import { useRouteParams } from '../../hooks/useRouteParams';
import { useFilterableYearOption } from '../../state/selectors';
import { queryKeys } from '../../api/queryKeys';
import { EntityDTO, EntityTypeDTO } from '../../DocumentVault.meta';

interface Props {
  entity: EntityDTO;
  onClose(): void;
}

const getFileNameParts = (fileName: string): { baseName: string; extension: string } => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    return { baseName: fileName, extension: '' };
  }
  return {
    baseName: fileName.substring(0, lastDotIndex),
    extension: fileName.substring(lastDotIndex),
  };
};

export const RenameEntity = ({ entity, onClose }: Props) => {
  const { clientId, path, providerType } = useRouteParams();
  const { value: year } = useFilterableYearOption();

  const queryClient = useQueryClient();

  const {
    mutateAsync: renameEntity,
    isLoading: isRenaming,
    error: renameError,
  } = useRenameEntityMutation();

  const isFile = entity.type === EntityTypeDTO.File;

  const { baseName, extension } = isFile
    ? getFileNameParts(entity.name)
    : { baseName: entity.name, extension: '' };

  const handleSubmit = async (newName: string) => {
    try {
      await renameEntity({
        clientId,
        objectPath: entity.path,
        newName,
      });

      queryClient.refetchQueries(
        queryKeys.listDocumentVaultEntities(clientId, path, providerType, year),
        {
          active: true,
        },
      );

      onClose();
    } catch (error) {
      // Error handling is managed by the error state from the mutation
    }
  };

  return (
    <EntityNameModal
      mode="rename"
      entityType={entity.type}
      initialName={baseName}
      extensionSuffix={extension}
      isLoading={isRenaming}
      error={renameError}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};
